import i18n from "../i18n";

export const DEFAULT_LANG = "en";

/**
 * Gets the current supported languages dynamically from i18n resources.
 * @returns {string[]}
 */
export const getSupportedLangs = () => {
    return Object.keys(i18n.options?.resources || {});
};

/**
 * Checks if a language code is supported.
 * @param {string} lang
 * @returns {boolean}
 */
export const isLangSupported = (lang) => {
    if (!lang) return false;
    return getSupportedLangs().includes(lang);
};

// Legacy constant for potential backward compatibility in other files, 
// though we aim to move towards dynamic checks.
export const SUPPORTED_LANGS = [
    "en",
    "ar",
    "es",
    "fa",
    "hy",
    "ru",
    "tl",
    "zh-CN",
    "ko",
    "hi-Guj",
];

/**
 * Extracts the language code from a path if it exists and is supported.
 * @param {string} path - URL pathname
 * @returns {string|null} - The language code or null
 */
export const getLangFromPath = (path) => {
    const parts = path.split("/").filter(Boolean);
    if (parts.length > 0 && isLangSupported(parts[0])) {
        return parts[0];
    }
    return null;
};

/**
 * Removes the language prefix from a path.
 * /en/services -> /services
 * /services -> /services
 * @param {string} path
 * @returns {string}
 */
export const stripLang = (path) => {
    const parts = path.split("/").filter(Boolean);
    if (parts.length > 0 && isLangSupported(parts[0])) {
        return "/" + parts.slice(1).join("/");
    }
    // If path was originally "/" -> parts=[], return "/"
    // If path was "/about" -> parts=["about"], return "/about"
    return path === "/" ? "/" : "/" + parts.join("/");
};

/**
 * Ensures the path has the specified language prefix.
 * Replaces existing supported prefix if present.
 * @param {string} path
 * @param {string} lang
 * @returns {string}
 */
export const withLang = (path, lang) => {
    if (!isLangSupported(lang)) lang = DEFAULT_LANG;
    const cleanPath = stripLang(path);
    // cleanPath starts with /, so we can just join
    return cleanPath === "/" ? `/${lang}` : `/${lang}${cleanPath}`;
};

/**
 * Swaps the current language in a path with a new one.
 * Alias for withLang since withLang handles replacement.
 */
export const swapLang = withLang;
/**
 * Maps non-standard internal language codes to valid BCP 47 tags for Intl APIs.
 * @param {string} lang 
 * @returns {string}
 */
export const getSafeLocale = (lang) => {
    const mappings = {
        "hi-Guj": "gu-IN",
    };
    return mappings[lang] || lang;
};
